import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';

import type { Amounts } from 'src/types/common/Amounts';
import useOfferErrors from 'src/inbox/hooks/useOfferErrors';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import CalendarAvailabilityManager from 'src/common/components/UpdateAvailability';
import useBoats from 'src/common/hooks/useBoats';
import useCustomOfferExpiry from '../../../../common/hooks/useCustomOfferExpiry';
import { CAPTAINED_FIELD, PAGES, OWNER_OFFER_EXPIRED_STATES, TRIP_ENDS_AT_FIELD, TRIP_STARTS_AT_FIELD, TRIP_STATES } from '../../../constants';
import { preserveSearch } from '../../../../common/utils/routing';
import TripPanel from '../../presentation/TripPanel';
import {
  DurationCard,
  DatesCard,
  GroupSizeCard,
  CaptainCard,
  CommentCard,
  TimeCard,
  VesselCard,
  DirectBookingCard,
} from '../../cards';
import TopScroll from '../../../../common/components/TopScroll';
import content, { getMessage } from './content';
import CTA, { CTALink, CTAWrapper } from '../../CTA';
import TripDetailsTitle from '../../presentation/TripDetailsTitle';
import TripMenu from '../../TripMenu';
import OfferSummary from '../../OfferSummary';
import OwnerPriceSection from '../../PriceSection/owner';
import DeclineMessages from '../../DeclineMessages';
import DateUnavailable from '../../DateUnavailable';
import { ImmutableTrip } from '../../../../types/trips/Trips';
import { getOffer, getTransaction } from '../../../../common/utils/reduxStoreSelectors';
import { getBoatMap } from '../../../../common/ducks/boatMap';
import { PATHS } from '../../../../common/constants';
import OfferError from '../../OfferError';

const SHOW_PROMPT_EXPIRATION_TIME = 3000; // Three seconds in milliseconds

const UPDATE_AVAILABILITY_STATES = [
  TRIP_STATES.OWNER_INQUIRY_DECLINED,
  TRIP_STATES.OWNER_OFFER_CANCELLED,
  TRIP_STATES.OWNER_RESERVATION_CANCELLED_BY_OWNER,
];

// Generic helper to check if any action is recent
const isRecentAction = (actionDate: string | undefined): boolean => {
  if (!actionDate) return false;

  const actionDateObj = new Date(actionDate);
  const now = new Date();
  // Calculate difference in milliseconds
  const diffMs = now.getTime() - actionDateObj.getTime();

  // Action is recent if it happened within the expiration time window
  return diffMs < SHOW_PROMPT_EXPIRATION_TIME;
};

type SendOfferProps = {
  cancellationDropdownOptions?: DropdownOption[];
  trip: ImmutableTrip;
};

const SendOffer: FC<SendOfferProps> = ({ cancellationDropdownOptions, trip }) => {
  const location = useLocation();
  const { url } = useRouteMatch();
  const boatMap = useSelector(getBoatMap);
  const offer = useSelector(getOffer);
  const transaction = useSelector(getTransaction).toJS();
  const amounts: Amounts = transaction?.amounts || trip?.getIn(['transaction', 'amounts'])?.toJS();
  const { renderCustomExpiry } = useCustomOfferExpiry(trip);
  const { hasErrors } = useOfferErrors(trip);
  const tripState = trip.getIn(['state', 'state']);
  const captained = trip.get(CAPTAINED_FIELD);
  const isDirectBooking = trip.get('direct_booking', false);

  let { boat } = trip.toJS();
  if (offer) {
    const offerBoatId = offer.getIn(['boat', 'id'], '');
    const tripBoatId = boat.id;
    if (offerBoatId && tripBoatId !== offerBoatId && boatMap[offerBoatId]) {
      boat = boatMap[offerBoatId].toJS();
    }
  }

  const { boatsIndexedById } = useBoats();
  const numericId = boat && boatsIndexedById[boat.id]?.boat_id;

  // Check for each specific action type
  const isInquiryDeclinedRecently = useMemo(
    () => isRecentAction(trip.getIn(['decline_message', 'date_created'])),
    [trip],
  );
  const isOfferCancelledRecently = useMemo(
    () => isRecentAction(trip.getIn(['cancel_message', 'date_created'])),
    [trip],
  );
  const isReservationCancelledRecently = useMemo(
    () => isRecentAction(trip.getIn(['reservation_cancel_message', 'date_created'])),
    [trip],
  );

  const showUpdateAvailabilityPrompt = useMemo(() => {
    if (!UPDATE_AVAILABILITY_STATES.includes(tripState)) {
      return false;
    }

    // Check the appropriate date based on trip state
    if (tripState === TRIP_STATES.OWNER_INQUIRY_DECLINED) {
      return isInquiryDeclinedRecently;
    } if (tripState === TRIP_STATES.OWNER_OFFER_CANCELLED) {
      return isOfferCancelledRecently;
    } if (tripState === TRIP_STATES.OWNER_RESERVATION_CANCELLED_BY_OWNER) {
      return isReservationCancelledRecently;
    }

    return false;
  }, [
    tripState,
    isInquiryDeclinedRecently,
    isOfferCancelledRecently,
    isReservationCancelledRecently,
  ]);

  const cta = (
    <CTA
      classNameModifier="withSidebar"
      withWarning={hasErrors}
    >
      {hasErrors && (<OfferError trip={trip} />)}
      <CTAWrapper>
        <TripMenu
          cancellationDropdownOptions={cancellationDropdownOptions}
          trip={trip}
        />
        <CTALink
          to={preserveSearch(`${url}${PAGES.PRICE}/`, location)}
          disabled={hasErrors}
          data-test="create-offer-button"
        >
          Create Offer
        </CTALink>
      </CTAWrapper>
    </CTA>
  );

  return (
    <TripPanel
      trip={trip}
      subheader={content.title[tripState] || trip.getIn(['state', 'badge'])}
      intro={getMessage({ trip, offer, location })}
      cta={cta}
      backLocation={preserveSearch(PATHS.INBOX, location)}
      classNameModifier="withoutSideMargins"
    >
      <TopScroll />
      <DeclineMessages trip={trip} />
      <DateUnavailable trip={trip} />
      {isDirectBooking && <DirectBookingCard trip={trip} />}
      {OWNER_OFFER_EXPIRED_STATES.includes(tripState) && (
        <OwnerPriceSection
          amounts={amounts}
          readOnly
          renderCustomExpiry={renderCustomExpiry}
          trip={trip}
        >
          <OfferSummary amounts={amounts} />
        </OwnerPriceSection>
      )}
      <TripDetailsTitle />
      <VesselCard trip={trip} />
      <DatesCard trip={trip} />
      <TimeCard
        trip={trip}
      />
      <DurationCard trip={trip} />
      <GroupSizeCard trip={trip} />
      {captained !== null && (
        <CaptainCard
          trip={trip}
        />
      )}
      <CommentCard trip={trip} />
      <CalendarAvailabilityManager
        boat={boat}
        boatId={numericId}
        endDateString={trip.getIn([TRIP_ENDS_AT_FIELD])}
        startDateString={trip.getIn([TRIP_STARTS_AT_FIELD])}
        showPrompt={showUpdateAvailabilityPrompt}
      />
    </TripPanel>
  );
};

export default SendOffer;
